import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { X, Headphones, Send, Loader2 } from 'lucide-react';
import { supportApi, SupportMessage } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface LiveSupportChatProps {
  onClose: () => void;
}

const LiveSupportChat: React.FC<LiveSupportChatProps> = ({ onClose }) => {
  const { currentUser, isAuthenticated } = useAuth();
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [ticketId, setTicketId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load existing ticket or create new one
  useEffect(() => {
    const loadOrCreateTicket = async () => {
      if (!isAuthenticated) return;

      setLoading(true);
      try {
        const result = await supportApi.getTickets();
        if (result.success && result.data?.tickets) {
          const openTicket = result.data.tickets.find(t => t.status === 'open' || t.status === 'pending');
          if (openTicket) {
            setTicketId(openTicket.id);
            // Load messages
            const msgResult = await supportApi.getTicketMessages(openTicket.id);
            if (msgResult.success && msgResult.data?.messages) {
              setMessages(msgResult.data.messages);
            }
          }
        }
      } catch (error) {
        console.error('Failed to load support ticket:', error);
      } finally {
        setLoading(false);
      }
    };

    loadOrCreateTicket();
  }, [isAuthenticated]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !isAuthenticated) return;

    setSending(true);
    try {
      if (!ticketId) {
        // Create new ticket
        const ticketResult = await supportApi.createTicket('Canli Destek', newMessage);
        if (ticketResult.success && ticketResult.data?.ticket) {
          setTicketId(ticketResult.data.ticket.id);
          // Add message to local state
          setMessages([{
            id: Date.now().toString(),
            ticketId: ticketResult.data.ticket.id,
            senderId: currentUser?.id || '',
            senderType: 'player',
            content: newMessage,
            createdAt: new Date().toISOString(),
          }]);
        }
      } else {
        // Send message to existing ticket
        const msgResult = await supportApi.sendTicketMessage(ticketId, newMessage);
        if (msgResult.success && msgResult.data?.message) {
          setMessages(prev => [...prev, msgResult.data!.message]);
        }
      }
      setNewMessage('');
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-dark-700/50">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center">
            <Headphones className="w-4 h-4 text-white" />
          </div>
          <div>
            <span className="text-sm font-medium text-white">Canli Destek</span>
            <div className="flex items-center space-x-1 text-xs text-gray-400">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              <span>Cevrimici</span>
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-dark-700/50 rounded transition-colors duration-200"
          title="Kapat"
        >
          <X className="w-4 h-4 text-gray-400 hover:text-white" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {!isAuthenticated ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Headphones className="w-12 h-12 text-gray-500 mb-4" />
            <p className="text-gray-400 text-sm">
              Canli destek icin giris yapin
            </p>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="w-16 h-16 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center mx-auto mb-4"
            >
              <Headphones className="w-8 h-8 text-white" />
            </motion.div>
            <h3 className="text-lg font-bold text-white mb-2">Merhaba!</h3>
            <p className="text-gray-400 text-sm">
              Size nasil yardimci olabiliriz?
            </p>
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.senderType === 'player' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-lg px-4 py-2 ${
                  msg.senderType === 'player'
                    ? 'bg-primary-500 text-white'
                    : 'bg-dark-700 text-gray-200'
                }`}
              >
                <p className="text-sm">{msg.content}</p>
                <span className="text-xs opacity-70 mt-1 block">
                  {new Date(msg.createdAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      {isAuthenticated && (
        <div className="p-4 border-t border-dark-700/50">
          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Mesajinizi yazin..."
              className="flex-1 bg-dark-700/50 border border-dark-600 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-primary-500 text-sm"
              disabled={sending}
            />
            <button
              onClick={handleSendMessage}
              disabled={!newMessage.trim() || sending}
              className="p-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
            >
              {sending ? (
                <Loader2 className="w-5 h-5 text-white animate-spin" />
              ) : (
                <Send className="w-5 h-5 text-white" />
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveSupportChat;
